/**
 * Core application state object
 * Central state management for the Audelio application
 */

import { TRANSCRIPT_CACHE_LIMIT } from '../config/constants.js';
import type { AppState as IAppState, ViewMode, Preferences, LibraryFilters, ClozeState, SequenceState, ClipDepth, ClipReplayCount, AudioRefs } from '../types/app-state.js';
import type { Clip } from '../types/clip.js';
import type { User } from '../types/rbac.js';

/**
 * Application state class
 */
class AppState implements IAppState {
  mode: ViewMode = 'home';
  clips: Clip[] = [];
  currentClipIdx: number = 0;
  currentQuestionIndex: number = 0;
  transcriptInjected: boolean = false;
  activeClipId: string | null = null;
  progressMap: Record<string, boolean> = {};
  preferences: Preferences = {};
  refs: AudioRefs = { audioEl: null };
  transcriptCache: Map<string, string> = new Map();
  transcriptScroll: Map<string, number> = new Map();
  transcriptPanelOpen: boolean = false;
  currentClip: Clip | null = null;
  clipAnswers: Map<string, boolean[]> = new Map();
  debugEventsEnabled: boolean = false;
  autoAdvanceTimeoutId: number | null = null;
  transcriptVisible: boolean = false;
  autoAdvanceActive: boolean = false;
  clipDepth: Map<string, ClipDepth> = new Map();
  clipReplayCount: Map<string, ClipReplayCount> = new Map();
  dailySet: string[] = [];
  libraryFilters: LibraryFilters = { difficulty: null, search: '' };
  seriesView: number | null = null;
  weeklyActivityDays: Set<string> = new Set();
  progressAnimationId: number | null = null;
  lastAriaUpdate: number = 0;
  clozeState?: ClozeState;
  sequenceState?: SequenceState;
  questionAttempts?: Record<string, number>;
  questionStartTime?: number;
  firstListenComplete: Record<string, boolean> = {};
  currentUser: User | null = null;
  
  constructor() {
    // Initialize debug events flag from localStorage
    try {
      this.debugEventsEnabled = localStorage.getItem('listenUpDev') === '1';
    } catch (e) {
      // localStorage might not be available
      this.debugEventsEnabled = false;
    }
  }
  
  /**
   * Manage transcript cache size to prevent memory growth
   */
  manageTranscriptCacheSize(): void {
    if (this.transcriptCache.size > TRANSCRIPT_CACHE_LIMIT) {
      // Get the first key (oldest entry in insertion order) and remove it
      const firstKey = this.transcriptCache.keys().next().value;
      if (firstKey !== undefined) {
        this.transcriptCache.delete(firstKey);
      }
    }
  }
}

// Create singleton instance
const appState = new AppState();

export default appState;

// Also export the class for potential testing purposes
export { AppState };
